import { faMinusCircle } from '@fortawesome/pro-light-svg-icons/faMinusCircle';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons/faPlusCircle';
import cx from 'classnames';
import React, { Component, PropsWithChildren, MouseEvent } from 'react';
import { Column, Dropdown, Icon, Row } from '../';
import { Label } from '../Label';
import { IListFilterItem, IListFilterProps } from './types';

import './ListFilter.scss';

const EMPTY_VAL = '';

interface IListFilterState<FilterDataType> {
  visibleItems: IListFilterItem<FilterDataType>[];
}

export class ListFilter<FilterDataType> extends Component<
  IListFilterProps<FilterDataType>,
  IListFilterState<FilterDataType>
> {
  constructor(props: IListFilterProps<FilterDataType>) {
    super(props);

    const itemsVisibleByDefault = props.items.filter((col) => !!props.filterData[col.filterKey as string]);

    if (itemsVisibleByDefault.length === 0) {
      itemsVisibleByDefault.push(props.items[0]);
    }

    this.state = {
      visibleItems: itemsVisibleByDefault,
    };
  }

  public render = () => {
    return (
      <div
        className={cx('hlc-list-filter', this.props.className)}
        qa-id={this.props['qa-id']}
        data-testid="list-filter"
      >
        {this.props.title && (
          <Row>
            <Column size={12}>
              <Label>{this.props.title}</Label>
            </Column>
          </Row>
        )}
        <Row>
          <Column size={12}>{this.renderFilterItems()}</Column>
        </Row>
        <Row section={true}>
          <Column size={12}>
            {this.canAddItem() && (
              <a className="add-item-btn" onClick={this.handleAddItem} href="#">
                <Icon icon={faPlusCircle} title="Add filter" />
                {' Add'}
              </a>
            )}
          </Column>
        </Row>
      </div>
    );
  };

  private renderFilterItems = () => {
    const usedKeys = this.state.visibleItems.map((col) => col.filterKey);
    const availableItems = this.props.items.filter((c) => usedKeys.indexOf(c.filterKey) < 0);
    const dropdownOptions = availableItems.map((col) => ({
      display: col.displayName,
      value: col.filterKey as string,
    }));

    return this.state.visibleItems.map((item) => (
      <div className="item-wrapper" key={item.filterKey} data-testid="list-filter-item">
        <div className="flex-container">
          <div className="item-selector">
            <Dropdown
              name="Filter Selector"
              options={[{ value: item.filterKey as string, display: item.displayName }].concat(dropdownOptions)}
              value={item.filterKey}
              onChange={(name, value) => this.handleSwitchFilterItem(item.filterKey, value as string)}
            />
          </div>
          <div className="item-value">
            {item.render(item.displayName, this.props.filterData[item.filterKey as string], (val) => {
              this.handleEditItem(item.filterKey, val);
            })}
          </div>
          <div className="item-controls">
            {this.canRemoveItem() && (
              <Icon
                className="remove-item-btn"
                icon={faMinusCircle}
                size="lg"
                title="Delete filter"
                onClick={() => this.handleRemoveItem(item.filterKey)}
              />
            )}
          </div>
        </div>
      </div>
    ));
  };

  private canAddItem = () => {
    return this.state.visibleItems.length < this.props.items.length;
  };

  private handleAddItem = (e: MouseEvent) => {
    const usedKeys = this.state.visibleItems.map((f) => f.filterKey);
    const newItem = this.props.items.find((c) => usedKeys.indexOf(c.filterKey) === -1);

    if (newItem) {
      const updatedItems = this.state.visibleItems.concat(newItem);
      this.setState({ visibleItems: updatedItems });
    }

    e.preventDefault();
  };

  private handleEditItem = (filterKey: string, value: string) => {
    const newFilterData = Object.assign({}, this.props.filterData, {
      [filterKey]: value,
    });
    this.props.onChange(newFilterData);
  };

  private canRemoveItem = () => {
    return this.state.visibleItems.length > 1;
  };

  private handleRemoveItem = (filterKey: string) => {
    const updatedItems = this.state.visibleItems.filter((f) => f.filterKey !== filterKey);

    this.setState({ visibleItems: updatedItems });

    const newFilterData = Object.assign({}, this.props.filterData, {
      [filterKey]: EMPTY_VAL,
    });
    this.props.onChange(newFilterData);
  };

  private handleSwitchFilterItem = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) {
      return;
    }

    const newItem = this.props.items.find((c) => c.filterKey === newKey) as IListFilterItem<FilterDataType>;

    const { visibleItems } = this.state;
    let oldItemIndex = null;

    for (let i = 0, max = visibleItems.length; i < max; i++) {
      if (visibleItems[i].filterKey === oldKey) {
        oldItemIndex = i;
        break;
      }
    }

    if (oldItemIndex !== null) {
      const itemsCopy = visibleItems.slice();
      itemsCopy.splice(oldItemIndex, 1, newItem);
      this.setState({ visibleItems: itemsCopy });

      const newFilterData = Object.assign({}, this.props.filterData, {
        [oldKey]: EMPTY_VAL,
        [newKey]: EMPTY_VAL,
      });
      this.props.onChange(newFilterData);
    }
  };
}
